import * as React from 'react';
import { FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';

import {
  IconRegularBadge,
  IconRegularDislike,
  IconRegularGift,
  IconRegularLike,
  IconSolidDislike,
  IconSolidLike,
  Ratings,
  Text,
  useBreakpointValue,
} from '@notino/react-styleguide';
import { AuthorType } from '@notino/shared/definitions/types';

import { Flex } from '@components/Flex';
import { PortalTooltip } from '@components/PortalTooltip';
import { VariantDisplay } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/FetchDoneBlock/ReviewItemDisplay/VariantDisplay';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';
import { ProsAndCons } from '@lib/reviews/ProsAndCons';
import { useGetReviewLabelComponents } from '@lib/reviews/useGetReviewLabelComponents';
import { getRelativeTimeProps } from '@utils/date';

import pdMessages from '../../../../../containers/ProductDetailContainer/messages';
import { messages as reviewMessages } from '../../../../../containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/messages';
import { useReviewFromCache, useReviewVote } from '../../hooks';
import { useReviewPhotoModalStore } from '../../store';

import {
  ContentWrapper,
  IconsWrapper,
  MoreInfoLink,
  ReviewContent,
  ReviewStats,
  ReviewText,
  SecondaryText,
  ThumbsUpDownWrapper,
  TitleAndVariantAndTextWrapper,
  TitleText,
  TooltipContent,
  Username,
} from './styled';
import { Thumbs } from './Thumbs';

export const Content = () => {
  const { product } = useProductDetailContext();
  const { formatMessage } = useIntl();
  const { shopId } = useApolloClientHeaders();
  const getLabels = useGetReviewLabelComponents();
  const isDesktop = useBreakpointValue({ md: true });
  const reviewId = useReviewPhotoModalStore((x) => x.reviewId);
  const review = useReviewFromCache(reviewId);
  const { like, dislike } = useReviewVote(review);
  const [showOriginal, setShowOriginal] = React.useState(false);

  const toggleShowOriginal = () => setShowOriginal((prev) => !prev);

  const displayText =
    review && showOriginal && review.originalText
      ? review.originalText
      : review?.text;
  const displayTitle =
    review && showOriginal && review.originalTitle
      ? review.originalTitle
      : review?.title;

  if (!review) {
    // should not happen, just in case
    return null;
  }

  const isVerified =
    review.authorType === AuthorType.Verified ||
    review.authorType === AuthorType.VerifiedPartner;
  const isMotivated = review.authorType === AuthorType.Motivated;

  const moreInfoLink = formatMessage(
    reviewMessages[`review${String(review.authorType)}MoreLink`]
  );

  const getUserNameOrCommentInfo = () => {
    if (review.isAnonymized) {
      const message =
        pdMessages[
          review.originalShopId
            ? 'anonymizedSharedComment'
            : 'anonymizedComment'
        ];

      return (
        <FormattedMessage
          {...message}
          values={{
            originalShopId: review.originalShopId ?? shopId,
          }}
        />
      );
    }

    return <span style={{ wordBreak: 'break-all' }}>{review.userName}</span>;
  };

  const getMoreLinkComponent = () => {
    return (
      moreInfoLink !== 'null' &&
      moreInfoLink && (
        <MoreInfoLink href={moreInfoLink} rel="nofollow" target="__blank">
          {formatMessage(reviewMessages.reviewMoreText)}
        </MoreInfoLink>
      )
    );
  };

  const labels = getLabels(review, showOriginal, toggleShowOriginal);
  const showVariant =
    !!review.variantId &&
    (product.variants.length > 1 ||
      product.variants[0].catalogId !== String(review.variantId));

  return (
    <ContentWrapper>
      {!isDesktop && <Thumbs />}

      <ReviewStats>
        <Flex direction="column" gap="8px">
          <Ratings
            ratingId="review-detail-modal-stars"
            size={14}
            rating={review.score}
            background="icon.disabled"
            foreground="icon.primary"
          />
          <Username>{getUserNameOrCommentInfo()}</Username>

          {(isVerified || isMotivated) && (
            <Flex gap="2px">
              <PortalTooltip
                interactive={true}
                content={
                  <PortalTooltip.Content>
                    <TooltipContent>
                      <FormattedMessage
                        {...reviewMessages[
                          `review${review.authorType as string}Description`
                        ]}
                      />

                      {getMoreLinkComponent()}
                    </TooltipContent>
                  </PortalTooltip.Content>
                }
              >
                <IconsWrapper>
                  {isVerified ? (
                    <IconRegularBadge
                      width="16px"
                      height="16px"
                      color="icon.positive"
                    />
                  ) : (
                    <IconRegularGift
                      width="16px"
                      height="16px"
                      color="icon.tertiary"
                    />
                  )}
                </IconsWrapper>
              </PortalTooltip>
              <Text
                typography="labelSmall"
                color={isMotivated ? 'text.tertiary' : 'text.positive'}
              >
                <FormattedMessage
                  {...reviewMessages[
                    `review${review.authorType as string}Title`
                  ]}
                />
              </Text>
            </Flex>
          )}
        </Flex>

        <SecondaryText>
          <FormattedRelativeTime
            {...getRelativeTimeProps(review.createdDate)}
          />
        </SecondaryText>
      </ReviewStats>

      <ReviewContent>
        <TitleAndVariantAndTextWrapper>
          {displayTitle && <TitleText>{displayTitle}</TitleText>}

          {showVariant && (
            <VariantDisplay catalogId={String(review.variantId)} />
          )}

          {displayText && <ReviewText>{displayText}</ReviewText>}
          {review.attributes && (
            <ProsAndCons
              style={{ marginTop: '16px' }}
              attributes={review.attributes}
            />
          )}
        </TitleAndVariantAndTextWrapper>

        {isDesktop && <Thumbs />}

        {labels.length > 0 && (
          <Flex direction="column" gap="12px">
            {labels}
          </Flex>
        )}

        <Flex margin="auto 0 0 0">
          <SecondaryText>
            <FormattedMessage {...pdMessages.reviewWasItUsefull} />
          </SecondaryText>

          <ThumbsUpDownWrapper>
            <button type="button" onClick={like}>
              {review.alreadyLiked ? <IconSolidLike /> : <IconRegularLike />}
              <SecondaryText>{review.like}</SecondaryText>
            </button>

            <button type="button" onClick={dislike}>
              {review.alreadyDisliked ? (
                <IconSolidDislike />
              ) : (
                <IconRegularDislike />
              )}
              <SecondaryText>{review.dislike}</SecondaryText>
            </button>
          </ThumbsUpDownWrapper>
        </Flex>
      </ReviewContent>
    </ContentWrapper>
  );
};
